/**
 * Application routing configuration
 * @author Maheen Nasir
 * @description Defines the application routes with lazy loading and suspense handling
 */

import { lazy, Suspense } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import type { ReactElement, ComponentType } from "react";
import Spinner from "@components/common/Spinner";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import { getEventManagementRoutes } from "@features/EventManagement/integration/routes";
import { getStaticCourseAssignRoutes } from "@features/CourseAssign/integration/routes";
import { IS_REPORT_MODULE_NAV_ENABLED } from "@features/ReportModule/reportModule.flags";
import AuthGuard from "@components/RouteGuards/AuthGuard";
import GuestGuard from "@components/RouteGuards/GuestGuard";
import ResetRouteGaurd from "@components/RouteGuards/ResetGuard";
import { MainLayout } from "@components/Layout/MainLayout";

// Lazy load components
const NotFound = lazy(() => import("@pages/404"));
const Unauthorized = lazy(() => import("@pages/Unauthorized"));
const InternalServerError = lazy(() => import("@pages/500"));
const Login = lazy(() => import("@pages/Login"));
const ForgetPassword = lazy(() => import("@pages/ForgetPassword"));
const VerifyOtp = lazy(() => import("@pages/VerifyOtp"));
const ResetPassword = lazy(() => import("@pages/ResetPassword"));
const SetPassword = lazy(() => import("@pages/SetPassword"));

const PrivacyPolicy = lazy(() => import("@pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("@pages/TermsAndConditions"));

// Main page components
const Dashboard = lazy(() => import("@pages/Dashboard"));
const Institutes = lazy(() => import("@pages/Institutes"));
const ApplicationsScrutiny = lazy(() => import("@pages/ApplicationsScrutiny"));
const ApplicationsScrutinized = lazy(() => import("@pages/ApplicationsScrutinized"));
const InspectedDirectors = lazy(() => import("@pages/InspectedDirectors"));
const ApplicationsRejected = lazy(() => import("@pages/ApplicationsRejected"));
const ApplicationsReferredBack = lazy(() => import("@pages/ApplicationsReferredBack"));
const ApplicationsApproved = lazy(() => import("@pages/ApplicationsApproved"));
const ApplicationsPendingRejection = lazy(() => import("@pages/ApplicationsPendingRejection"));

const VerifyApplication = lazy(() => import("@pages/VerifyApplication"));
const Certificates = lazy(() => import("@pages/Certificates"));
const Members = lazy(() => import("@pages/Members"));
const Appeals = lazy(() => import("@pages/Appeals"));
const CommitteesMy = lazy(() => import("@pages/CommitteesMy"));
const CommitteesHistory = lazy(() => import("@pages/CommitteesHistory"));
const CommitteesRequests = lazy(() => import("@pages/CommitteesRequests"));
const CourseBuilder = lazy(() => import("@pages/CourseBuilder"));
const Calendar = lazy(() => import("@pages/Calendar"));
const ManageSessions = lazy(() => import("@pages/ManageSessions"));
const Reports = lazy(() => import("@pages/Reports"));
const EmployeeEndorsement = lazy(() => import("@pages/EmployeeEndorsement"));
const ReportModule = lazy(() => import("@pages/ReportModule"));
const ManageUsers = lazy(() => import("@pages/ManageUsers"));
const RolesAndPermissions = lazy(() => import("@pages/RolesAndPermissions"));
const AuditLogs = lazy(() => import("@pages/AuditLogs"));
const Configuration = lazy(() => import("@pages/Configuration"));
const Traceability = lazy(() => import("@pages/Traceability"));
const FAQSections = lazy(() => import("@pages/FAQSections"));
const FAQItems = lazy(() => import("@pages/FAQItems"));
const UnauthorizedInstituteReports = lazy(() => import("@pages/UnauthorizedInstituteReports"));
const ApprenticeshipIndustry = lazy(() => import("@pages/ApprenticeshipIndustry"));
const ApprenticeshipStatistics = lazy(() => import("@pages/ApprenticeshipStatistics"));
const ApprenticeshipApprentices = lazy(() => import("@pages/ApprenticeshipApprentices"));
const ApprenticeshipOutcomes = lazy(() => import("@pages/ApprenticeshipOutcomes"));

// Inspection components
const DirectorInspection = lazy(() => import("@pages/DirectorInspection"));

/**
 * Higher-order component to wrap lazy-loaded components with Suspense
 * @param {React.LazyExoticComponent<ComponentType>} Component - The lazy-loaded component to wrap
 * @returns {JSX.Element} The wrapped component with Suspense
 */
const withSuspense = (Component: React.LazyExoticComponent<ComponentType>): ReactElement => {
	return (
		<Suspense fallback={<Spinner overlay />}>
			<Component />
		</Suspense>
	);
};

/**
 * Application route configuration
 * @description Defines the main routes of the application with their corresponding components
 */
export const routes: RouteObject[] = [
	{
		path: "/",
		element: (
			<AuthGuard>
				<MainLayout />
			</AuthGuard>
		),
		children: [
			{ index: true, element: withSuspense(Dashboard) },
			{ path: BROWSER_ROUTES.INSTITUTES, element: withSuspense(Institutes) },
			{ path: BROWSER_ROUTES.APPLICATIONS_SCRUTINY, element: withSuspense(ApplicationsScrutiny) },
			{ path: BROWSER_ROUTES.APPLICATIONS_SCRUTINIZED, element: withSuspense(ApplicationsScrutinized) },
			{ path: BROWSER_ROUTES.APPLICATIONS_INSPECTED, element: withSuspense(InspectedDirectors) },
			{ path: BROWSER_ROUTES.APPLICATIONS_REJECTED, element: withSuspense(ApplicationsRejected) },
			{ path: BROWSER_ROUTES.APPLICATIONS_REFERRED_BACK, element: withSuspense(ApplicationsReferredBack) },
			{ path: BROWSER_ROUTES.APPLICATIONS_APPROVED, element: withSuspense(ApplicationsApproved) },
			{ path: BROWSER_ROUTES.APPLICATIONS_PENDING_REJECTION, element: withSuspense(ApplicationsPendingRejection) },
			{ path: BROWSER_ROUTES.VERIFY_APPLICATION, element: withSuspense(VerifyApplication) },
			{ path: BROWSER_ROUTES.COMMITTEES_MY, element: withSuspense(CommitteesMy) },
			{ path: BROWSER_ROUTES.COMMITTEES_HISTORY, element: withSuspense(CommitteesHistory) },
			{ path: BROWSER_ROUTES.COMMITTEES_REQUESTS, element: withSuspense(CommitteesRequests) },
			{ path: BROWSER_ROUTES.CERTIFICATES, element: withSuspense(Certificates) },
			{ path: BROWSER_ROUTES.MEMBERS, element: withSuspense(Members) },
			{ path: BROWSER_ROUTES.APPEALS, element: withSuspense(Appeals) },
			{ path: BROWSER_ROUTES.FAQ_SECTIONS, element: withSuspense(FAQSections) },
			{ path: BROWSER_ROUTES.FAQ_ITEMS, element: withSuspense(FAQItems) },
			{ path: BROWSER_ROUTES.UNAUTHORIZED_INSTITUTE_REPORTS, element: withSuspense(UnauthorizedInstituteReports) },
			{ path: BROWSER_ROUTES.COURSE_BUILDER, element: withSuspense(CourseBuilder) },
			{ path: BROWSER_ROUTES.COURSE_BUILDER_EQUIPMENT, element: withSuspense(CourseBuilder) },
			{ path: BROWSER_ROUTES.COURSE_BUILDER_LABS, element: withSuspense(CourseBuilder) },
			{ path: BROWSER_ROUTES.COURSE_BUILDER_COURSES, element: withSuspense(CourseBuilder) },
			{ path: BROWSER_ROUTES.CALENDAR, element: withSuspense(Calendar) },
			...getEventManagementRoutes(withSuspense),
			...getStaticCourseAssignRoutes(withSuspense),
			{ path: BROWSER_ROUTES.MANAGE_SESSIONS, element: withSuspense(ManageSessions) },
			{ path: BROWSER_ROUTES.REPORTS, element: withSuspense(Reports) },
			{ path: BROWSER_ROUTES.EMPLOYEE_ENDORSEMENT, element: withSuspense(EmployeeEndorsement) },
			{
				path: BROWSER_ROUTES.REPORT_MODULE,
				element: IS_REPORT_MODULE_NAV_ENABLED ? (
					withSuspense(ReportModule)
				) : (
					<Navigate to={BROWSER_ROUTES.DASHBOARD} replace />
				),
			},
			{ path: BROWSER_ROUTES.TRACEABILITY, element: withSuspense(Traceability) },
			{ path: BROWSER_ROUTES.APPRENTICESHIP_INDUSTRY, element: withSuspense(ApprenticeshipIndustry) },
			{ path: BROWSER_ROUTES.APPRENTICESHIP_STATISTICS, element: withSuspense(ApprenticeshipStatistics) },
			{ path: BROWSER_ROUTES.APPRENTICESHIP_APPRENTICES, element: withSuspense(ApprenticeshipApprentices) },
			{ path: BROWSER_ROUTES.APPRENTICESHIP_OUTCOMES, element: withSuspense(ApprenticeshipOutcomes) },
			{ path: BROWSER_ROUTES.MANAGE_USERS, element: withSuspense(ManageUsers) },
			{ path: BROWSER_ROUTES.ROLES_AND_PERMISSIONS, element: withSuspense(RolesAndPermissions) },
			{ path: BROWSER_ROUTES.AUDIT_LOGS, element: withSuspense(AuditLogs) },
			{ path: BROWSER_ROUTES.CONFIGURATION, element: withSuspense(Configuration) },
			{ path: BROWSER_ROUTES.DIRECTOR_INSPECTION, element: withSuspense(DirectorInspection) },
		],
	},
	{
		path: BROWSER_ROUTES.LOGIN,
		element: <GuestGuard>{withSuspense(Login)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.FORGET_PASSWORD,
		element: <GuestGuard>{withSuspense(ForgetPassword)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.VERIFY_OTP,
		element: <GuestGuard>{withSuspense(VerifyOtp)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.RESET_PASSWORD,
		element: (
			<GuestGuard>
				<ResetRouteGaurd>{withSuspense(ResetPassword)}</ResetRouteGaurd>
			</GuestGuard>
		),
	},
	{
		path: BROWSER_ROUTES.SET_PASSWORD,
		element: withSuspense(SetPassword),
	},
	{
		path: BROWSER_ROUTES.UNAUTHORIZED,
		element: withSuspense(Unauthorized),
	},
	{
		path: BROWSER_ROUTES.SERVER_ERROR,
		element: withSuspense(InternalServerError),
	},
	{
		path: BROWSER_ROUTES.PRIVACY_POLICY,
		element: withSuspense(PrivacyPolicy),
	},
	{
		path: BROWSER_ROUTES.TERMS_AND_CONDITIONS,
		element: withSuspense(TermsAndConditions),
	},
	{
		path: "*",
		element: withSuspense(NotFound),
	},
];

export default routes;
